import React from 'react';
import { AlertCircle, Users, Navigation, Shield, MapPin, ArrowRight } from 'lucide-react';

const EmergencyResponse: React.FC = () => {
  // Mock data - in real app this would come from floor sensors and emergency systems
  const emergencyData = {
    currentStatus: {
      occupants: 342,
      activeAlerts: 0,
      securedZones: 15,
      availableExits: 8
    },
    evacuationRoutes: [
      {
        id: 1,
        name: 'North Exit Route',
        status: 'Clear',
        occupantCount: 0,
        estimatedTime: '2:30',
        congestionLevel: 'Low'
      },
      {
        id: 2,
        name: 'Main Lobby Route',
        status: 'Clear',
        occupantCount: 0,
        estimatedTime: '3:15',
        congestionLevel: 'Low'
      },
      {
        id: 3,
        name: 'East Emergency Exit',
        status: 'Partially Blocked',
        occupantCount: 0,
        estimatedTime: '4:00',
        congestionLevel: 'High'
      },
      {
        id: 4,
        name: 'Service Exit Route',
        status: 'Clear',
        occupantCount: 0,
        estimatedTime: '2:45',
        congestionLevel: 'Low'
      }
    ],
    zoneStatus: [
      {
        zone: 'North Wing',
        occupants: 85,
        nearestExit: 'North Exit',
        status: 'Clear'
      },
      {
        zone: 'South Wing',
        occupants: 92,
        nearestExit: 'Main Lobby',
        status: 'Clear'
      },
      {
        zone: 'East Wing',
        occupants: 78,
        nearestExit: 'East Exit',
        status: 'Caution'
      },
      {
        zone: 'West Wing',
        occupants: 87,
        nearestExit: 'Service Exit',
        status: 'Clear'
      }
    ],
    recentDrills: [
      {
        date: '2024-03-01',
        totalTime: '4:15',
        participants: 325,
        issues: 'Minor congestion at Main Lobby'
      },
      {
        date: '2024-02-01',
        totalTime: '4:30',
        participants: 312,
        issues: 'None reported'
      },
      {
        date: '2024-01-02',
        totalTime: '4:45',
        participants: 298,
        issues: 'East Exit bottleneck'
      }
    ],
    firstResponderInfo: {
      assemblyPoints: [
        { name: 'North Parking Lot', capacity: 200, current: 0 },
        { name: 'South Garden', capacity: 150, current: 0 },
        { name: 'East Plaza', capacity: 175, current: 0 }
      ],
      keyAccess: [
        { location: 'Fire Control Room', level: 'B1' },
        { location: 'Security Office', level: '1' },
        { location: 'Utility Room', level: 'B2' },
        { location: 'Roof Access', level: 'R' }
      ]
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'clear':
        return 'text-green-500';
      case 'caution':
        return 'text-yellow-500';
      case 'blocked':
      case 'danger':
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  };

  const getCongestionColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'low':
        return 'bg-green-100 text-green-800';
      case 'moderate':
        return 'bg-yellow-100 text-yellow-800';
      case 'high':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Emergency Response Planning</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Users className="text-blue-500 mr-2" />
            <h3 className="text-lg font-semibold">Current Occupants</h3>
          </div>
          <p className="text-3xl font-bold">{emergencyData.currentStatus.occupants}</p>
          <p className="text-gray-500">People in building</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <AlertCircle className="text-red-500 mr-2" />
            <h3 className="text-lg font-semibold">Active Alerts</h3>
          </div>
          <p className="text-3xl font-bold">{emergencyData.currentStatus.activeAlerts}</p>
          <p className="text-gray-500">Emergency situations</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Shield className="text-green-500 mr-2" />
            <h3 className="text-lg font-semibold">Secured Zones</h3>
          </div>
          <p className="text-3xl font-bold">{emergencyData.currentStatus.securedZones}</p>
          <p className="text-gray-500">Protected areas</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Navigation className="text-purple-500 mr-2" />
            <h3 className="text-lg font-semibold">Available Exits</h3>
          </div>
          <p className="text-3xl font-bold">{emergencyData.currentStatus.availableExits}</p>
          <p className="text-gray-500">Clear evacuation routes</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Evacuation Routes</h3>
          <div className="space-y-4">
            {emergencyData.evacuationRoutes.map((route) => (
              <div key={route.id} className="border-b pb-4 last:border-b-0">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-medium">{route.name}</h4>
                    <p className={`text-sm ${getStatusColor(route.status)}`}>
                      {route.status}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs ${getCongestionColor(route.congestionLevel)}`}>
                    {route.congestionLevel}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-sm text-gray-500">
                    Est. Time: {route.estimatedTime}
                  </span>
                  <span className="text-sm text-gray-500">
                    {route.occupantCount} people
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Zone Status</h3>
          <div className="space-y-4">
            {emergencyData.zoneStatus.map((zone, index) => (
              <div key={index} className="flex items-center justify-between border-b pb-4 last:border-b-0">
                <div>
                  <p className="font-medium">{zone.zone}</p>
                  <p className="text-sm text-gray-500">
                    Exit: {zone.nearestExit}
                  </p>
                </div>
                <div className="text-right">
                  <p className={`text-sm font-medium ${getStatusColor(zone.status)}`}>
                    {zone.status}
                  </p>
                  <p className="text-sm text-gray-500">
                    {zone.occupants} occupants
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Recent Emergency Drills</h3>
          <div className="space-y-4">
            {emergencyData.recentDrills.map((drill, index) => (
              <div key={index} className="border-b pb-4 last:border-b-0">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium">{drill.date}</h4>
                    <p className="text-sm text-gray-600">
                      {drill.participants} participants
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{drill.totalTime}</p>
                    <p className="text-sm text-gray-500">Total time</p>
                  </div>
                </div>
                {drill.issues && (
                  <p className="text-sm text-yellow-600 mt-2">
                    Note: {drill.issues}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">First Responder Information</h3>
          <div className="space-y-6">
            <div>
              <h4 className="font-medium mb-3">Assembly Points</h4>
              <div className="space-y-3">
                {emergencyData.firstResponderInfo.assemblyPoints.map((point, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{point.name}</p>
                      <p className="text-sm text-gray-500">
                        Capacity: {point.capacity}
                      </p>
                    </div>
                    <div className="flex items-center">
                      <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                        <div
                          className="bg-blue-500 rounded-full h-2"
                          style={{ width: `${(point.current / point.capacity) * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-sm">{point.current}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h4 className="font-medium mb-3">Key Access Points</h4>
              <div className="grid grid-cols-2 gap-3">
                {emergencyData.firstResponderInfo.keyAccess.map((access, index) => (
                  <div key={index} className="bg-gray-50 rounded p-3">
                    <p className="font-medium">{access.location}</p>
                    <p className="text-sm text-gray-500">Level {access.level}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmergencyResponse; 